/**
 * /log/:realtorId/:userId/:sig — PUBLIC one-tap interaction logger.
 *
 * Linked from the "✓ Log Interaction" URL embedded in exported contact
 * cards. No login: the URL carries an HMAC signature that only allows
 * creating an interaction entry for this one realtor, attributed to the
 * user who exported the card.
 *
 * Flow: tap link in iOS Contacts → tiny page with 5 big buttons →
 * Call / Text / Email log instantly (optional note after) → Note shows a
 * text field → Referral expands client + fee + inspection-date fields.
 */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Phone, MessageSquare, Mail, StickyNote, Handshake,
  CheckCircle2, Loader2, AlertTriangle, ArrowLeft, ExternalLink,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const TYPE_META = {
  call: { label: "Call", icon: Phone },
  sms: { label: "Text", icon: MessageSquare },
  email: { label: "Email", icon: Mail },
  note: { label: "Note", icon: StickyNote },
  referral: { label: "Referral", icon: Handshake },
};

export default function LogInteraction() {
  const { realtorId, userId, sig } = useParams();
  const tokenPath = `/log-token/${realtorId}/${userId}/${sig}`;

  const [info, setInfo] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [mode, setMode] = useState("menu"); // menu | note | referral | done
  const [busy, setBusy] = useState(false);
  const [logged, setLogged] = useState(null); // {id, type}
  const [note, setNote] = useState("");
  const [noteSaved, setNoteSaved] = useState(false);
  const [refClient, setRefClient] = useState("");
  const [refFee, setRefFee] = useState("");
  const [refDate, setRefDate] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(tokenPath);
        setInfo(data);
      } catch (e) {
        setLoadError(formatApiError(e));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenPath]);

  const post = async (payload) => {
    setBusy(true);
    try {
      const { data } = await api.post(tokenPath, payload);
      setLogged({ id: data.interaction_id, type: payload.type });
      setMode("done");
      setNoteSaved(false);
      return true;
    } catch (e) {
      setLoadError(formatApiError(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const quickLog = (type) => post({ type });
  const submitNote = () => post({ type: "note", note: note.trim() });
  const submitReferral = () =>
    post({
      type: "referral",
      referral_client: refClient.trim() || null,
      referral_fee: refFee ? parseFloat(refFee) : null,
      referral_inspection_date: refDate || null,
      note: note.trim() || null,
    });

  const saveNoteToLogged = async () => {
    if (!logged?.id || !note.trim()) return;
    setBusy(true);
    try {
      await api.patch(`${tokenPath}/${logged.id}`, { note: note.trim() });
      setNoteSaved(true);
    } catch (e) {
      setLoadError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setMode("menu");
    setLogged(null);
    setNote("");
    setNoteSaved(false);
    setRefClient("");
    setRefFee("");
    setRefDate("");
    setLoadError("");
  };

  const realtor = info?.realtor;
  const initials = (realtor?.name || "?")
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="min-h-screen bg-slate-100 flex items-start justify-center px-4 py-8" data-testid="log-interaction-page">
      <div className="w-full max-w-sm">
        <div className="text-center mb-4">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">
            <span className="h-5 w-5 rounded bg-[#C8102E] text-white flex items-center justify-center text-[10px] font-bold">RC</span>
            Realtor Outreach CRM
          </div>
        </div>

        <div className="rounded-2xl bg-white shadow-lg border border-slate-200 overflow-hidden">
          {!info && !loadError ? (
            <div className="flex items-center justify-center py-16 text-slate-500 text-sm" data-testid="log-loading">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading…
            </div>
          ) : loadError && !info ? (
            <div className="p-6 text-center" data-testid="log-invalid">
              <AlertTriangle className="h-8 w-8 mx-auto text-amber-500 mb-2" />
              <p className="text-sm font-medium text-slate-900">This link isn&apos;t valid</p>
              <p className="text-xs text-slate-500 mt-1">{loadError}</p>
            </div>
          ) : (
            <>
              {/* Realtor header. Hide the photo when it's a known broken
                  placeholder (e.g. Realtor.ca's 1x1 fallback or an FB icon
                  SVG) — plain initials look better than a broken image. */}
              <div className="px-5 pt-5 pb-4 flex items-center gap-3 border-b border-slate-100">
                {realtor?.photo_url && !(realtor?.photo_meta?.is_placeholder === true) ? (
                  <img
                    src={realtor.photo_url}
                    alt=""
                    className="h-12 w-12 rounded-full object-cover bg-slate-200"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                ) : (
                  <div className="h-12 w-12 rounded-full bg-slate-800 text-white flex items-center justify-center font-semibold">
                    {initials}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 truncate" data-testid="log-realtor-name">
                    {realtor?.name}
                  </div>
                  <div className="text-xs text-slate-500 truncate">{realtor?.brokerage_name || "—"}</div>
                </div>
              </div>

              {mode === "menu" ? (
                <div className="p-4 space-y-2">
                  <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold px-1">
                    Log an interaction
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <BigButton type="call" onClick={() => quickLog("call")} disabled={busy} />
                    <BigButton type="sms" onClick={() => quickLog("sms")} disabled={busy} />
                    <BigButton type="email" onClick={() => quickLog("email")} disabled={busy} />
                    <BigButton type="note" onClick={() => setMode("note")} disabled={busy} />
                  </div>
                  <BigButton type="referral" onClick={() => setMode("referral")} disabled={busy} wide />
                  {busy ? (
                    <div className="flex items-center justify-center text-xs text-slate-500 pt-1">
                      <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> Logging…
                    </div>
                  ) : null}
                </div>
              ) : null}

              {mode === "note" ? (
                <div className="p-4 space-y-3" data-testid="log-note-form">
                  <BackRow onBack={reset} label="Log a note" />
                  <Textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="What happened? (e.g. chatted at the open house on Preston Ave)"
                    rows={4}
                    autoFocus
                    data-testid="log-note-input"
                  />
                  <Button
                    className="w-full min-h-[48px] text-white"
                    style={{ background: "#C8102E" }}
                    disabled={busy || !note.trim()}
                    onClick={submitNote}
                    data-testid="log-note-save"
                  >
                    {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <StickyNote className="h-4 w-4 mr-2" />}
                    Save note
                  </Button>
                </div>
              ) : null}

              {mode === "referral" ? (
                <div className="p-4 space-y-3" data-testid="log-referral-form">
                  <BackRow onBack={reset} label="Log a referral" />
                  <div>
                    <label className="text-xs font-medium text-slate-600">Client name (optional)</label>
                    <Input
                      value={refClient}
                      onChange={(e) => setRefClient(e.target.value)}
                      placeholder="e.g. Bob Smith"
                      className="mt-1 min-h-[44px]"
                      data-testid="log-referral-client"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs font-medium text-slate-600">Inspection fee ($)</label>
                      <Input
                        type="number"
                        inputMode="decimal"
                        value={refFee}
                        onChange={(e) => setRefFee(e.target.value)}
                        placeholder="650"
                        className="mt-1 min-h-[44px]"
                        data-testid="log-referral-fee"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600">Inspection date</label>
                      <Input
                        type="date"
                        value={refDate}
                        onChange={(e) => setRefDate(e.target.value)}
                        className="mt-1 min-h-[44px]"
                        data-testid="log-referral-date"
                      />
                    </div>
                  </div>
                  <Textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Notes (optional)"
                    rows={2}
                    data-testid="log-referral-note"
                  />
                  <Button
                    className="w-full min-h-[48px] text-white"
                    style={{ background: "#C8102E" }}
                    disabled={busy}
                    onClick={submitReferral}
                    data-testid="log-referral-save"
                  >
                    {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Handshake className="h-4 w-4 mr-2" />}
                    Save referral
                  </Button>
                </div>
              ) : null}

              {mode === "done" && logged ? (
                <div className="p-5 text-center space-y-3" data-testid="log-success">
                  <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-500" />
                  <div>
                    <p className="font-semibold text-slate-900">
                      {TYPE_META[logged.type]?.label || "Interaction"} logged for {realtor?.name}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">Just now · saved to the CRM timeline</p>
                  </div>
                  {["call", "sms", "email"].includes(logged.type) && !noteSaved ? (
                    <div className="text-left space-y-2 pt-1">
                      <Textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Add a quick note (optional)"
                        rows={2}
                        data-testid="log-success-note-input"
                      />
                      <Button
                        variant="outline"
                        className="w-full min-h-[44px]"
                        disabled={busy || !note.trim()}
                        onClick={saveNoteToLogged}
                        data-testid="log-success-note-save"
                      >
                        {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                        Save note
                      </Button>
                    </div>
                  ) : noteSaved ? (
                    <p className="text-xs text-emerald-700 font-medium" data-testid="log-note-saved">✓ Note saved</p>
                  ) : null}
                  <div className="pt-1 space-y-2">
                    <Button
                      variant="ghost"
                      className="w-full min-h-[44px] text-slate-600"
                      onClick={reset}
                      data-testid="log-another-button"
                    >
                      Log another interaction
                    </Button>
                    <a
                      href={`/realtors/${realtor?.id}`}
                      className="inline-flex items-center justify-center gap-1.5 text-xs text-slate-500 hover:text-slate-800"
                      data-testid="log-open-crm-link"
                    >
                      <ExternalLink className="h-3 w-3" /> Open full profile in CRM
                    </a>
                  </div>
                </div>
              ) : null}

              {loadError && info ? (
                <p className="px-4 pb-4 text-xs text-rose-600" data-testid="log-error">{loadError}</p>
              ) : null}
            </>
          )}
        </div>

        <p className="text-center text-[11px] text-slate-400 mt-4">
          {info?.logger_name ? `Logging as ${info.logger_name} · ` : ""}This link only logs
          interactions for this contact.
        </p>
      </div>
    </div>
  );
}

function BigButton({ type, onClick, disabled, wide = false }) {
  const meta = TYPE_META[type];
  const Icon = meta.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={
        "rounded-xl border border-slate-200 bg-white hover:border-slate-400 hover:bg-slate-50 " +
        "active:scale-[0.98] transition-all min-h-[64px] flex flex-col items-center justify-center gap-1 " +
        "disabled:opacity-50 " + (wide ? "w-full" : "")
      }
      data-testid={`log-btn-${type}`}
    >
      <Icon className="h-5 w-5 text-[#C8102E]" />
      <span className="text-sm font-semibold text-slate-800">{meta.label}</span>
    </button>
  );
}

function BackRow({ onBack, label }) {
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={onBack}
        className="h-8 w-8 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-500"
        data-testid="log-back-button"
      >
        <ArrowLeft className="h-4 w-4" />
      </button>
      <span className="text-sm font-semibold text-slate-900">{label}</span>
    </div>
  );
}
